import { useCallback, useEffect, useState } from 'react';
import {
	GetLotusBlossomPurchases,
	LotusBlossomPurchase,
	PurchasesByEmail,
} from 'api/lotusBlossom/lotusBlossomAPI';
import { replaceMembership } from '../libs/purchases';

export interface LotusBlossomPurchasesState {
	/** Every purchase keyed by lowercased email; null until Stripe has answered. */
	purchases: PurchasesByEmail | null;
	/** Stripe couldn't be read. `purchases` is then {} so the rosters still render. */
	failed: boolean;
}

export interface LotusBlossomPurchases extends LotusBlossomPurchasesState {
	/**
	 * Puts a membership the server just cancelled or resumed in place, so every
	 * tab shows the change without paging Stripe again.
	 */
	updateMembership: (updated: LotusBlossomPurchase) => void;
}

/**
 * Reads purchases once for the whole page. It pages Stripe, which can take
 * tens of seconds, so the rosters render without waiting for it and fill in
 * their purchase columns when it lands — and switching tabs or sessions doesn't
 * start another read.
 */
export function useLotusBlossomPurchases(): LotusBlossomPurchases {
	const [state, setState] = useState<LotusBlossomPurchasesState>({
		purchases: null,
		failed: false,
	});

	useEffect(() => {
		let cancelled = false;

		GetLotusBlossomPurchases()
			.then((purchases) => {
				if (!cancelled) setState({ purchases, failed: false });
			})
			.catch((error) => {
				// A Stripe outage costs the purchase columns, not the rosters.
				console.error('[QuantumHealing] purchase history unavailable:', error);
				if (!cancelled) setState({ purchases: {}, failed: true });
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const updateMembership = useCallback((updated: LotusBlossomPurchase) => {
		setState((current) =>
			current.purchases
				? { ...current, purchases: replaceMembership(current.purchases, updated) }
				: current
		);
	}, []);

	return { ...state, updateMembership };
}

export default useLotusBlossomPurchases;
